import _extends from "@babel/runtime/helpers/extends";

/** @jsx jsx */
import { forwardRef, memo, useMemo } from 'react';
import { css, jsx } from '@emotion/react';
import { usePlatformLeafEventHandler } from '@atlaskit/analytics-next/usePlatformLeafEventHandler';
import __noop from '@atlaskit/ds-lib/noop';
import { DN600, N80, N900 } from '@atlaskit/theme/colors';
import GlobalTheme from '@atlaskit/theme/components';
import { fontFamily as getFontFamily } from '@atlaskit/theme/constants';
import getRadioCustomProperties from './styles';
const packageName = "@atlaskit/radio";
const packageVersion = "5.4.3";
const fontFamily = getFontFamily();
const noop = __noop;
const labelPaddingStyles = css({
  padding: `${"var(--ds-scale-025, 2px)"} ${"var(--ds-scale-050, 4px)"}`
});
const labelStyles = css({
  display: 'flex',
  boxSizing: 'border-box',
  position: 'relative',
  alignItems: 'flex-start',
  fontFamily: fontFamily,
  // eslint-disable-next-line @repo/internal/styles/no-nested-styles
  '&[data-disabled]': {
    color: `var(--ds-text-disabled, ${N80})`,
    cursor: 'not-allowed'
  }
});
const lightLabelStyles = css({
  color: `var(--ds-text, ${N900})`
});
const darkLabelStyles = css({
  color: `var(--ds-text, ${DN600})`
});
const radioStyles = css({
  display: 'flex',

  /*
    The circle should be 14px * 14px centred in a 24px * 24px box.
    This is inclusive of a 2px border and inner circle with 2px radius.
    There is a Chrome bug that makes the circle become an oval and the
    inner circle not be centred at various zoom levels. This bug is fixed
    in all browsers if a scale of 14/24 is applied.
  */
  width: '24px',
  height: '24px',
  // TODO Delete this comment after verifying spacing token -> previous value `0`
  margin: "var(--ds-scale-0, 0px)",
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  backgroundColor: 'var(--radio-background-color)',

  /* Border should be 2px, multiply by 24/14 to offset scale */
  border: 'calc(2px * 12 / 7) solid var(--radio-border-color)',
  borderRadius: '50%',
  MozAppearance: 'none',
  outline: 'none',

  /*
    Change the variables --radio-background-color, --radio-border-color,
    -radio-dot-color and -radio-dot-opacity according to user interactions.
    All other variables are constant
  */
  '--radio-background-color': 'var(--local-background)',
  '--radio-border-color': 'var(--local-border)',
  '--radio-dot-color': 'var(--local-dot-checked)',
  '--radio-dot-opacity': 0,

  /* 24px * 7 / 12 === 14px height and width */
  transform: 'scale(calc(7 / 12))',
  transition: 'border-color 0.2s ease-in-out, background-color 0.2s ease-in-out',
  verticalAlign: 'top',
  WebkitAppearance: 'none',
  '&::after': {
    /* Height and width should by 4px, multiply by 24/14 to offset scale */
    width: 'calc(4px * 12 / 7)',
    height: 'calc(4px * 12 / 7)',
    position: 'absolute',
    background: 'var(--radio-dot-color)',
    borderRadius: '50%',
    content: "''",
    opacity: 'var(--radio-dot-opacity)',
    transition: 'background-color 0.2s ease-in-out, opacity 0.2s ease-in-out'
  },
  '&:hover': {
    '--radio-background-color': 'var(--local-background-hover)',
    '--radio-border-color': 'var(local-border-hover)'
  },
  '&:active': {
    '--radio-background-color': 'var(--local-background-active)'
  },
  '&:focus': {
    '--radio-border-color': 'var(--local-border-focus)'
  },
  '&:checked': {
    '--radio-background-color': 'var(--local-background-checked)',
    '--radio-border-color': 'var(--local-background-checked)',
    '--radio-dot-opacity': 1
  },
  '&:checked:hover': {
    '--radio-background-color': 'var(--local-background-checked-hover)',
    '--radio-border-color': 'var(--local-background-checked-hover)'
  },
  '&:checked:active': {
    '--radio-background-color': 'var(--local-background-checked-active)',
    '--radio-border-color': 'var(--local-border-hover)',
    '--radio-dot-color': 'var(--local-dot-active)'
  },
  '&:checked:focus': {
    '--radio-border-color': 'var(--local-border-focus)'
  },
  // eslint-disable-next-line @repo/internal/styles/no-nested-styles
  '&[data-invalid], &:checked[data-invalid]': {
    '--radio-border-color': 'var(--local-invalid)'
  },
  '&:disabled, &:disabled:hover, &:disabled:focus, &:disabled:active, &:disabled[data-invalid]': {
    cursor: 'not-allowed',
    '--radio-background-color': 'var(--local-background-disabled)',
    '--radio-border-color': 'var(--local-border-disabled)',
    '--radio-dot-color': 'var(--local-dot-disabled)'
  },
  '&:checked:disabled': {
    '--radio-border-color': 'transparent' // TODO: Investigate token solution

  }
});
const RadioWithMode = /*#__PURE__*/forwardRef(function Radio(props, ref) {
  const {
    ariaLabel,
    isDisabled = false,
    isRequired,
    isInvalid = false,
    isChecked = false,
    label,
    mode,
    name,
    onChange = noop,
    value,
    testId,
    analyticsContext,
    // events and all other input props
    ...rest
  } = props;
  const onChangeAnalytics = usePlatformLeafEventHandler({
    fn: onChange,
    action: 'changed',
    analyticsData: analyticsContext,
    componentName: 'radio',
    packageName,
    packageVersion
  });
  const radioCustomProperties = useMemo(() => css(getRadioCustomProperties(mode)), [mode]);
  return (// https://product-fabric.atlassian.net/browse/DST-1971
    // eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for
    jsx("label", {
      "data-testid": testId && `${testId}--radio-label`,
      "data-disabled": isDisabled ? 'true' : undefined // TODO these will no longer be dynamic styles when legacy theming removed
      // eslint-disable-next-line @repo/internal/react/consistent-css-prop-usage
      ,
      css: [labelStyles, mode === 'light' ? lightLabelStyles : darkLabelStyles]
    }, jsx("input", _extends({}, rest, {
      "aria-label": ariaLabel,
      checked: isChecked,
      disabled: isDisabled,
      name: name,
      onChange: onChangeAnalytics,
      required: isRequired,
      type: "radio",
      value: value,
      "data-testid": testId && `${testId}--radio-input` // isInvalid is used in a nonstandard way so cannot
      // use :invalid selector
      ,
      "data-invalid": isInvalid ? 'true' : undefined // TODO radioCustomProperties can be defined at top of file when legacy theming removed
      // eslint-disable-next-line @repo/internal/react/consistent-css-prop-usage
      ,
      css: [radioStyles, radioCustomProperties],
      ref: ref
    })), label ? jsx("span", {
      css: labelPaddingStyles
    }, label) : null)
  );
});
/**
 * __Radio__
 *
 * A radio input allows users to select only one option from a number of choices. Radio is generally displayed in a radio group.
 *
 * - [Examples](https://atlassian.design/components/radio/examples)
 * - [Code](https://atlassian.design/components/radio/code)
 * - [Usage](https://atlassian.design/components/radio/usage)
 */

const Radio = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(function Radio(props, ref) {
  return jsx(GlobalTheme.Consumer, null, ({
    mode
  }) => jsx(RadioWithMode, _extends({}, props, {
    ref: ref,
    mode: mode
  })));
}));
export default Radio;